// src/utils/buildLaunchReport.js
//
// WP5 (docs/launch-signoff-spec.md §7): self-contained HTML sign-off report
// for a Launch workspace record. Mirrors buildSignOffReport.js's
// architecture — esc() helper, verdict banner styling, an ackSection with
// checkbox-gating inline <script>, Blob-based open/download — but rolls up
// PER CRITERION (via utils/launchRollup.js) instead of per-slot.
//
// buildLaunchReport(launch, resultsByCheckId, { tester, runId, app }) -> HTML
// string. Never throws — the whole build is wrapped in try/catch and
// degrades to a minimal valid HTML error page on any failure.
//
//   launch           — { id, title, ticket, app, build, criteria:[...],
//                        suite, runIds, signoff } (WP2 §5 shape; any field
//                        may be missing/null)
//   resultsByCheckId — plain object OR Map, checkId -> latest evaluateCheck()
//                        result ({ checkId, status, matched, found,
//                        failures, rows:[{requestType,url,id,requestId,ts,
//                        read,ok}], note? }). May be empty/null — every
//                        criterion then renders 'not-run', which is honest.
//   tester            — string, defaults to ''
//   runId             — the run just used to produce resultsByCheckId, optional
//   app               — app id/name override for the header, optional
//                        (falls back to launch.app)
//   checkDefsById     — Map (or plain object) of checkId -> Check definition
//                        (proxy-server/conformance schema: {id, description,
//                        target, assert, severity} — see GET
//                        /proxy/launches/<id>/checks / LaunchPage.jsx's
//                        checkDefsById). Optional; when absent (undefined,
//                        null, or a checkId simply missing from it) every
//                        check renders an explicit "definition unavailable"
//                        note instead of blank — see describeCheck.js, whose
//                        describeCheck/describeAssert/describeScope exports
//                        this module reuses rather than re-deriving check
//                        prose itself.
//   capture           — optional { label, at, scope, device }: which traffic
//                        this verdict is about (the Launch page's active
//                        source + scope caption). Rendered in the header
//                        when present, omitted entirely when not — never
//                        invented.

import { buildRequestInventory } from './requestInventory';
import { requestLink } from './deepLinks';
import {
  CRITERION_STATUS,
  CRITERION_STATUS_LABEL,
  criterionStatus,
  criterionResults,
  effectiveNaReason,
  COVERAGE_GAP_REASONS,
  NO_BRANCH_UNIT,
} from './launchRollup';
import { WORDING, fill, captureGapHint } from './launchWording';
import { describeCheck } from './describeCheck';
import { decodeA3p } from './a3pDecode';
import {
  checkSentence, criterionSentence, toConfirmLine, scopeLine,
  overallVerdict, limitsOfCapture, observations, captureFacts, reasonFor, mixedNeedsReview, floorDowngrade,
} from './reportWording';
import { evidenceLine, manualEvidenceLine, unmappedEvidenceLine, describeSource, describeExpectation } from './evidenceLine';
import { sdkBiddingSectionFor } from './sdkBiddingSectionHtml';

// ── small shared helpers (copied from buildSignOffReport.js rather than
// imported — that module isn't structured for reuse as a library) ─────────

function esc(s = '') {
  return String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
}

function shortText(s, max = 70) {
  const str = String(s ?? '');
  return str.length > max ? `${str.slice(0, max)}…` : str;
}


// ── criterion-status chip styling (5-tier: met/not-met/needs-review/
// manual-review/not-run — see launchRollup.js) ─────────────────────────────

const STATUS_STYLE = {
  [CRITERION_STATUS.MET]: { color: '#1a6f3c', bg: '#edfaf3' },
  [CRITERION_STATUS.NOT_MET]: { color: '#8a1a1a', bg: '#fff0f0' },
  [CRITERION_STATUS.NEEDS_REVIEW]: { color: '#854f0b', bg: '#fffbea' },
  [CRITERION_STATUS.MANUAL_REVIEW]: { color: '#1c5f8a', bg: '#eef6fc' },
  [CRITERION_STATUS.NOT_RUN]: { color: '#666666', bg: '#f5f5f5' },
};

function statusChip(status) {
  const s = STATUS_STYLE[status] || STATUS_STYLE[CRITERION_STATUS.NOT_RUN];
  const label = CRITERION_STATUS_LABEL[status] || 'Not run';
  return `<span style="display:inline-block;padding:2px 10px;border-radius:4px;font-weight:700;font-size:11px;background:${s.bg};color:${s.color};border:1px solid ${s.color}40">${esc(label)}</span>`;
}

const CHECK_STATUS_STYLE = {
  pass: { color: '#1a6f3c', bg: '#edfaf3', label: 'PASS' },
  warn: { color: '#854f0b', bg: '#fffbea', label: 'WARN' },
  fail: { color: '#8a1a1a', bg: '#fff0f0', label: 'FAIL' },
  info: { color: '#1c5f8a', bg: '#eef6fc', label: 'INFO' },
  'n/a': { color: '#666666', bg: '#f5f5f5', label: 'N/A' },
  na: { color: '#666666', bg: '#f5f5f5', label: 'N/A' },
};

function checkStatusChip(status) {
  const s = CHECK_STATUS_STYLE[status] || { color: '#999999', bg: '#f5f5f5', label: 'NOT RUN' };
  return `<span style="display:inline-block;padding:1px 7px;border-radius:4px;font-weight:700;font-size:10px;background:${s.bg};color:${s.color};border:1px solid ${s.color}40">${esc(s.label)}</span>`;
}

// ── evidence rows shown per criterion, preferring !ok entries ──────────────
//
// Production incident: a sign-off report showed a check's appendix row as
// "MATCHED 13 / FOUND 0", but the criterion table above it carried only 3
// evidence rows with nothing telling the reader that 10 more were withheld
// — a reader cannot tell "3 of 3" (clean, small check) from "3 of 13"
// (most of the evidence hidden) apart. That is exactly the "silent cap"
// this repo has an explicit discipline against, and it happened on the one
// artifact people actually sign. Do NOT quietly lower EVIDENCE_DISPLAY_CAP
// again without also keeping the "showing N of M matched requests" note
// below it truthful — the cap itself is fine, silence about it is not.
//
// The rows are already fully in memory (evaluateCheck() itself caps
// upstream at EVIDENCE_ROW_CAP=50 — proxy-server/conformance/
// evaluateCheck.js), so raising the display cap costs nothing; only the
// render was throwing rows away.
const EVIDENCE_DISPLAY_CAP = 10;

function pickEvidence(checks, cap = EVIDENCE_DISPLAY_CAP) {
  const all = [];
  let totalMatched = 0;
  let rowsTruncatedUpstream = false;
  for (const { result } of checks) {
    const rows = Array.isArray(result?.rows) ? result.rows : [];
    for (const r of rows) if (r) all.push(r);
    if (typeof result?.matched === 'number') totalMatched += result.matched;
    if (result?.rowsTruncated) rowsTruncatedUpstream = true;
  }
  const bad = all.filter((r) => r.ok === false);
  const rest = all.filter((r) => r.ok !== false);
  const rows = [...bad, ...rest].slice(0, cap);
  return { rows, shown: rows.length, totalMatched, rowsTruncatedUpstream };
}

// Renders the "N more withheld" / "truncated upstream" notes under a
// criterion's evidence rows. Two distinct causes, both worth naming:
//  - this report's own display cap (EVIDENCE_DISPLAY_CAP) hid rows that
//    ARE in memory — compares what's shown against the check(s)' `matched`.
//  - evaluateCheck()'s upstream EVIDENCE_ROW_CAP (50) already dropped rows
//    before they ever reached the frontend (result.rowsTruncated) — those
//    rows never arrived here at all, so no display cap can recover them.
// Both can be true on the same criterion at once.
function evidenceCapNotesHtml({ shown, totalMatched, rowsTruncatedUpstream }) {
  const bits = [];
  if (totalMatched > shown) {
    bits.push(`<div style="font-size:11px;color:#8a5a1a;font-weight:700">⚠ Showing ${shown} of ${totalMatched} matched requests — ${totalMatched - shown} more not shown here (see the appendix for full matched/found counts).</div>`);
  }
  if (rowsTruncatedUpstream) {
    bits.push('<div style="font-size:11px;color:#8a5a1a">Evidence truncated upstream at 50 rows — more requests matched than were retained to show at all.</div>');
  }
  return bits.join('');
}

// ── the request's parameters, with what caused the verdict highlighted ──
// Replaces a raw URL dump: long values (encrypted ms / a3p blobs) are cut to a
// preview + their length, cust_params is expanded into its key-values, and the
// params the failing finding or the check's own field name are marked ◀.
const PARAM_PREVIEW = 80;
function focusKeysFor(def, readVal) {
  const keys = new Set();
  const a = def && def.assert;
  const own = a && (a.param ?? a.path ?? a.key);
  if (own) keys.add(String(own).split('.').pop());
  if (a && a.source === 'custParams') keys.add('cust_params');
  // Only the findings that can fail a check name its cause. The read value joins
  // every finding, and an info one ("info|secure_signal_bundle … a3p") marked a3p
  // as "caused this" next to the real malformed pair.
  const segs = String(readVal || '').split(/(?:^|,\s*)(?=(?:required|fail|error|warn|warning|info|optional|pass)\|)/i).filter(Boolean);
  const text = segs.some((x) => /^(info|optional|pass)\|/i.test(x))
    ? segs.filter((x) => !/^(info|optional|pass)\|/i.test(x)).join(', ')
    : String(readVal || '');
  for (const m of text.matchAll(/"([A-Za-z0-9_.-]{2,40})"/g)) keys.add(m[1]);           // quoted names in findings
  for (const m of text.matchAll(/\b(cust_params|us_privacy|gdpr|gpp|iu|sz|msz|adtest|ppid|rdid|a3p|correlator|is_lat)\b/g)) keys.add(m[1]);
  return keys;
}
function paramValueHtml(v) {
  const s = String(v);
  return s.length > PARAM_PREVIEW
    ? `${esc(s.slice(0, PARAM_PREVIEW))}… <span class="muted">(${s.length} chars)</span>`
    : esc(s);
}
function paramLines(url, def, readVal, failed) {
  let u;
  try { u = new URL(url); } catch { return null; }
  const focus = failed ? focusKeysFor(def, readVal) : new Set();
  const line = (k, v, hot, note = '') => `<div class="param${hot ? ' hot' : ''}"><code>${esc(k)}</code> = <code>${paramValueHtml(v)}</code>${hot ? ` <b class="hot-mark">◀ ${esc(note || 'caused this')}</b>` : ''}</div>`;
  const out = [];
  for (const [k, v] of u.searchParams.entries()) {
    if (k === 'cust_params') {
      const pairs = String(v).split('&').filter(Boolean);
      const anyHot = focus.has('cust_params') || pairs.some((kv) => focus.has(kv.split('=')[0]));
      out.push(line(k, `${pairs.length} key-values`, anyHot && !pairs.some((kv) => focus.has(kv.split('=')[0]) || !kv.includes('=')), 'cust_params'));
      for (const kv of pairs) {
        const eq = kv.indexOf('=');
        const ik = eq === -1 ? kv : kv.slice(0, eq);
        const iv = eq === -1 ? '(no "=" — malformed pair)' : (kv.slice(eq + 1) || '(empty value)');
        const hot = failed && (focus.has(ik) || (focus.has('cust_params') && (eq === -1 || iv === '(empty value)')));
        out.push(`<div style="padding-left:16px">${line(ik, iv, hot, eq === -1 ? 'malformed pair' : 'caused this')}</div>`);
      }
      continue;
    }
    out.push(line(k, v, focus.has(k)));
  }
  const hot = out.filter((h) => h.includes('class="param hot"') || h.includes('hot-mark'));
  return { where: u.host + u.pathname, hot, rest: out.filter((h) => !hot.includes(h)) };
}
function requestParamsHtml(url, def, readVal, failed) {
  const p = paramLines(url, def, readVal, failed);
  if (!p) return url ? `<code>${esc(shortText(url, 300))}</code>` : '';
  // Hot params first so the cause is visible without scrolling the list.
  return `<div class="params"><div class="small muted">${esc(p.where)}</div>${[...p.hot, ...p.rest].join('')}</div>`;
}

// ── a3p: a check that reads Secure Signals shows the decoded adapter list, not
// the 2,000-char blob. The buyer the check is about (its pattern, else every SDK
// bidding buyer) is highlighted; a gzipped bid token is inflated by the page's
// script on open (DecompressionStream is async, this builder is not).
function isA3pCheck(def) {
  return /a3p|secureSignal/i.test(JSON.stringify([def && def.assert, def && def.target && def.target.where]) || '');
}
function a3pHtml(url, def) {
  let u; try { u = new URL(url); } catch { return ''; }
  const raw = u.searchParams.get('a3p');
  if (!raw) return `<div class="params small muted">${esc(u.host + u.pathname)} · no <code>a3p</code> on this request</div>`;
  const d = decodeA3p(raw);
  if (!d.ok) return `<div class="params small muted">${esc(u.host + u.pathname)} · <code>a3p</code> (${raw.length} chars): ${esc(d.error)}</div>`;
  let re = null;
  try { re = def && def.assert && def.assert.pattern ? new RegExp(def.assert.pattern, 'i') : null; } catch { re = null; }
  if (!re || !d.entries.some((e) => re.test(e.adapter) || re.test(e.buyer))) re = null;
  const isHot = (e) => (re ? re.test(e.adapter) || re.test(e.buyer) : !/UID2|LiveRamp/.test(e.buyer));
  const entry = (e) => {
    const hot = isHot(e);
    const ver = [e.sdkVersion && `SDK ${e.sdkVersion}`, e.adapterVersion && `adapter ${e.adapterVersion}`].filter(Boolean).join(' · ');
    const sig = e.gzipBase64
      ? `<div class="a3p-token" data-gz="${esc(e.gzipBase64)}">bid token: gzipped, ${e.signalLength} chars (decoded when this page opens)</div>`
      : `<div class="small muted">signal: ${e.signalLength ? `<code>${paramValueHtml(e.signal)}</code>` : 'empty'}</div>`;
    return `<div class="a3p-entry${hot ? ' hot' : ''}"><b>${esc(e.buyer)}</b>${hot ? ' <b class="hot-mark">◀</b>' : ''} · <code>${esc(e.adapter)}</code>${ver ? ` · ${esc(ver)}` : ''}${hot ? sig : ''}</div>`;
  };
  return `<div class="params"><div class="small muted">${esc(u.host + u.pathname)} · <code>a3p</code> decoded: ${d.entries.length} adapter${d.entries.length === 1 ? '' : 's'}, ${d.bytes} bytes</div>${d.entries.map(entry).join('')}</div>`;
}

// ── the one param the check reads (a cust_params key such as `c`, or a top-level
// URL param), printed on EVERY example, pass or fail, so a reader sees the value
// that decided it without opening the full list.
function checkedParamHtml(url, def, failed) {
  const a = def && def.assert;
  if (!a || !['custParams', 'url', 'find'].includes(a.source)) return '';
  const key = a.param ?? a.key;
  if (!key || typeof key !== 'string') return '';
  let u; try { u = new URL(url); } catch { return ''; }
  const pairs = String(u.searchParams.get('cust_params') || '').split('&').filter(Boolean)
    .map((kv) => { const i = kv.indexOf('='); return i === -1 ? [kv, null] : [kv.slice(0, i), kv.slice(i + 1)]; });
  const inCust = pairs.filter(([k]) => k === key);
  const top = a.source === 'custParams' ? [] : u.searchParams.getAll(key);
  const cls = failed ? 'param hot' : 'param checked';
  const mark = `<b class="${failed ? 'hot-mark' : 'checked-mark'}">◀ ${failed ? 'failed here' : 'checked'}</b>`;
  const line = (label, v) => `<div class="${cls}"><code>${esc(label)}</code> = <code>${v == null ? '(no &quot;=&quot;)' : (v === '' ? '(empty value)' : paramValueHtml(v))}</code> ${mark}</div>`;
  const lines = [
    ...inCust.map(([k, v]) => line(`cust_params › ${k}`, v)),
    ...top.map((v) => line(key, v)),
  ];
  const where = `<div class="small muted">${esc(u.host + u.pathname)}</div>`;
  if (!lines.length) {
    return `<div class="params">${where}<div class="${cls}"><code>${esc(a.source === 'custParams' ? `cust_params › ${key}` : key)}</code> — not on this request ${mark}</div></div>`;
  }
  return `<div class="params">${where}${lines.join('')}</div>`;
}

function evidenceRowHtml(r, def = null) {
  // Print-time mask: the embedded row.url stays clear in the app (ChecksPage join), but the exported report must not carry it.
  const url = launchMaskText(r?.url || '');
  let where = url;
  try { const u = new URL(url); where = u.host + u.pathname; } catch { /* keep the raw string */ }
  // r.read == null means the assert read nothing off this row. In a sign-off
  // report that must not print a bare "(absent)": a reader can't tell a clean
  // absence check from a check that found nothing to look at. Say which.
  const readParam = def && def.assert ? (def.assert.param ?? def.assert.path ?? def.assert.key) : undefined;
  // masked at print time too: a result from an older proxy may carry an ID read in clear
  const readVal = r?.read != null ? shortText(launchSafeValue(r.read, readParam), 100) : 'no value read';
  const type = r?.requestType ? esc(r.requestType) : '—';
  const link = requestLink(r?.requestId);
  const okMark = r?.ok === false
    ? '<span style="color:#8a1a1a;font-weight:700">✗</span>'
    : (r?.ok ? '<span style="color:#1a6f3c;font-weight:700">✓</span>' : '');
  const when = r?.at ?? r?.ts;
  const t = when != null ? new Date(typeof when === 'number' ? when : Date.parse(when)) : null;
  const time = t && !Number.isNaN(t.getTime()) ? `${t.toISOString().slice(11, 19)} UTC · ` : '';
  const head = `${okMark} ${esc(time)}<code>${type}</code> · ${esc(shortText(where, 90))}`;
  // What was checked on THIS request, against what — so the line stands on its own.
  const a = def && def.assert;
  const checkedLine = a
    ? `<div style="padding-left:14px">checked <code>${esc(describeSource(a.source, a.param ?? a.path ?? a.key))}</code>: read <b>${esc(readVal)}</b> · expected <b>${esc(describeExpectation(a))}</b></div>`
    : `<div style="padding-left:14px">read: <b>${esc(readVal)}</b></div>`;
  // The facts a reader needs without the HAR: ad unit, sizes, winner, bid, buyer, IDs.
  const facts = r?.facts && typeof r.facts === 'object' ? Object.entries(r.facts) : [];
  const factsLine = facts.length
    ? `<div style="padding-left:14px;color:#555">${facts.map(([k, v]) => `${esc(k)}: <code>${esc(shortText(launchSafeValue(v, k), 80))}</code>`).join(' · ')}</div>`
    : '';
  // The exact request, copyable — what "how did it confirm this?" needs.
  const nParams = (() => { try { return [...new URL(url).searchParams.keys()].length; } catch { return 0; } })();
  // A failing row prints only the params that caused it; the full list (~90 on a
  // GAM request) stays folded — five open lists were 12 of a 38-page PDF.
  const cause = r?.ok === false && !isA3pCheck(def) ? paramLines(url, def, r?.read, true) : null;
  const checked = url && !isA3pCheck(def) ? checkedParamHtml(url, def, r?.ok === false) : '';
  const causeHtml = isA3pCheck(def) && url ? a3pHtml(url, def)
    : checked || (cause && cause.hot.length ? `<div class="params">${cause.hot.join('')}</div>` : '');
  const full = url
    ? `<details class="req-url"><summary>request parameters (${nParams})</summary>${requestParamsHtml(url, def, r?.read, r?.ok === false)}</details>`
    : '';
  return `<div style="font-size:11px;color:#444;padding:3px 0">${
    link ? `<a href="${esc(link)}" target="_blank" rel="noopener noreferrer">${head}</a>` : head
  }${checkedLine}${factsLine}${causeHtml}${full}</div>`;
}

// "Proved by" / "Failed on": the requests that decided this check, deciding rows
// first (ok:true for a pass, ok:false for a fail), capped so a card stays readable.
const PROOF_ROWS = 3;
function proofRowsHtml(result, def = null) {
  const rows = Array.isArray(result?.rows) ? result.rows.filter(Boolean) : [];
  if (!rows.length) {
    // Never silently blank: say why there is no example.
    if (!result) return '<div class="small muted">No example request: this check was not part of the run.</div>';
    const r = reasonFor(null, result, def);
    return `<div class="small muted">No example request: ${esc(r ? r.text : 'no request in scope matched this check')}.</div>`;
  }
  const failed = result?.status === 'fail' || result?.status === 'warn';
  // Only the DECIDING requests here; the criterion's pooled "Evidence" list below
  // keeps everything that was looked at, so the two never repeat each other.
  const deciding = rows.filter((r) => (failed ? r.ok === false : r.ok === true));
  // At least one example request per check: when no row decided the verdict,
  // show one request the check looked at, so the reader still sees what it read.
  if (!deciding.length) {
    const looked = typeof result?.matched === 'number' ? result.matched : rows.length;
    return `<div class="proof"><div class="small" style="font-weight:600;margin-top:4px">Example request looked at · 1 of ${looked} (none decided this check)</div>${evidenceRowHtml(rows[0], def)}</div>`;
  }
  const shown = deciding.slice(0, PROOF_ROWS);
  const label = failed ? 'Failed on' : 'Proved by';
  // Same counts as the check sentence above (satisfied/failed of matched over ALL
  // rows), not the 50-row evidence sample, so the two numbers never disagree.
  const looked = typeof result?.matched === 'number' ? result.matched : rows.length;
  const total = failed
    ? (Array.isArray(result?.failures) ? result.failures.length : deciding.length)
    : (typeof result?.satisfied === 'number' ? result.satisfied : deciding.length);
  const more = total > shown.length ? ` <span class="small muted">— showing ${shown.length}; all ${total} are in the evidence HAR</span>` : '';
  return `<div class="proof"><div class="small" style="font-weight:600;margin-top:4px">${label} · ${total} of ${looked} request${looked === 1 ? '' : 's'}${more}</div>${shown.map((r) => evidenceRowHtml(r, def)).join('')}</div>`;
}

// ── check DEFINITIONS (checkDefsById), tolerant of Map, plain object, or
// entirely absent — what closes the "'Met' was unauditable" gap. A checkId
// with no entry here (suite edited after the run, fetch failed, or the map
// itself was never passed by an older call site) must render an explicit
// "definition unavailable" note, never a blank cell — a blank reads as "this
// check asserts nothing", which is worse than not showing it at all. ───────

function getCheckDef(checkDefsById, checkId) {
  try {
    if (!checkDefsById || !checkId) return null;
    if (typeof checkDefsById.get === 'function') return checkDefsById.get(checkId) || null;
    if (typeof checkDefsById === 'object') return checkDefsById[checkId] || null;
    return null;
  } catch {
    return null;
  }
}

const DEF_UNAVAILABLE_NOTE = 'Check definition unavailable — the suite may have changed since this run, or the definitions failed to load. What this check asserts cannot be shown.';

// One short line for the criterion table's already-dense Checks column: what
// the check grades, from describeCheck()'s human-readable description (or
// its derived assertion sentence when description is blank).
function checkDetailLine(checkId, checkDefsById) {
  const def = getCheckDef(checkDefsById, checkId);
  if (!def) {
    return `<div style="font-size:10px;color:#8a5a1a;font-style:italic;margin-top:1px">⚠ ${esc(DEF_UNAVAILABLE_NOTE)}</div>`;
  }
  const d = describeCheck(def);
  // The rule as a one-sentence requirement when the suite carries one, else
  // the description, else the derived assertion.
  const grades = (d.statement && d.statement.trim()) || (d.description && d.description.trim()) || d.rule;
  // One line only — the full description, scope and assertion are under
  // "Checks tested"; the card should read as a verdict, not a suite dump.
  return `<div class="small">${esc(shortText(grades, 140))}</div>`;
}

// ── resultsByCheckId iteration, tolerant of Map or plain object ────────────

function resultEntries(resultsByCheckId) {
  try {
    if (!resultsByCheckId) return [];
    if (typeof resultsByCheckId.entries === 'function' && typeof resultsByCheckId.get === 'function') {
      return Array.from(resultsByCheckId.entries());
    }
    return Object.entries(resultsByCheckId);
  } catch {
    return [];
  }
}

function fmtDateTime(d) {
  const dateStr = d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const timeStr = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  return { dateStr, timeStr };
}

function uniq(arr) {
  const seen = new Set();
  const out = [];
  for (const v of arr) {
    if (v == null || v === '') continue;
    if (seen.has(v)) continue;
    seen.add(v);
    out.push(v);
  }
  return out;
}

// ── main build ───────────────────────────────────────────────────────────


// ── "Requests graded": every GAM / Prebid / APS request in the run, its params,
// and which checks failed or warned on it (requestInventory.js). Absent when the
// caller passed no rows (e.g. a report regenerated without the session loaded).
function requestInventoryHtml(rows, resultsByCheckId, checkDefsById, winners) {
  const fams = buildRequestInventory(rows, resultsByCheckId, checkDefsById, winners);
  if (!fams.length) return '';
  const kv = (o) => Object.entries(o || {}).map(([k, v]) => `${esc(k)}=${esc(v)}`).join(' · ') || '—';
  const list = (a) => (Array.isArray(a) && a.length ? a.map(esc).join(', ') : '—');
  const time = (t) => { const d = new Date(typeof t === 'number' ? t : Date.parse(t)); return Number.isFinite(d.getTime()) ? d.toLocaleTimeString() : '—'; };
  const verdict = (i) => i.status === 'pass' ? '<span class="ok">✓ pass</span>'
    : `<span class="${i.status === 'fail' ? 'bad' : 'warn-text'}">${i.status === 'fail' ? '✗ fail' : '⚠ warn'}</span><div class="small">${i.problems.map((p) => esc(p.displayName)).join('; ')}</div>`;
  const section = (f) => {
    const head = f.key === 'gam' ? '<th>#</th><th>Time</th><th>Request</th><th>iu</th><th>sz</th><th>correlator</th><th>Key-values (cust_params)</th><th>Won by</th><th>Result</th>'
      : f.key === 'prebid' ? '<th>#</th><th>Time</th><th>Request</th><th>Auction id</th><th>imp ids</th><th>tagids</th><th>Sizes</th><th>Bidders</th><th>Result</th>'
      : '<th>#</th><th>Time</th><th>Request</th><th>Slots</th><th>Sizes</th><th>Result</th>';
    const body = f.items.map((i, n) => {
      const id = `<code>${esc(i.id || '—')}</code><div class="small muted">${esc(i.host || '')}${i.captureSource === 'logcat' ? ' · USB' : ''}</div>`;
      const p = i.params || {};
      const cells = f.key === 'gam' ? `<td>${esc(p.iu || '—')}</td><td>${esc(p.sz || '—')}</td><td>${esc(p.correlator || '—')}</td><td class="small">${kv(p.kvps)}</td><td>${i.winner ? `<b>${esc(i.winner.label)}</b><div class="small muted">${esc(i.winner.detail || '')}</div>` : '<span class="small muted">—</span>'}</td>`
        : f.key === 'prebid' ? (p.bodyMissing ? `<td colspan="5" class="small muted">${p.usb ? 'USB capture: the request body is not in the device log' : 'request body not kept in this browser (bodies are kept for the newest 300 requests); the evidence HAR has it'}</td>` : `<td>${esc(p.requestId || '—')}</td><td>${list(p.impIds)}</td><td>${list(p.tagids)}</td><td>${list(p.sizes)}</td><td>${list(p.bidders)}</td>`)
        : (p.bodyMissing && !(p.slots || []).length ? '<td colspan="2" class="small muted">request body not available</td>' : `<td>${list(p.slots)}</td><td>${list(p.sizes)}</td>`);
      return `<tr><td>${n + 1}</td><td class="small">${time(i.ts)}</td><td>${id}</td>${cells}<td>${verdict(i)}</td></tr>`;
    }).join('');
    const passed = f.total - f.failed - f.warned;
    return `<h3>${esc(f.label)} — ${f.total} total · ${f.failed} failed · ${f.warned} warned · ${passed} passed</h3>
      ${f.truncated ? `<div class="small warn-text">Showing the first ${f.items.length}; ${f.truncated} more are in the run.</div>` : ''}
      ${Object.keys(f.wonBy || {}).length ? `<div class="small"><b>Won by:</b> ${Object.entries(f.wonBy).sort((a, b) => b[1] - a[1]).map(([k, n]) => `${esc(k)} ${n}`).join(' · ')}</div>` : ''}
      <table class="inventory"><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`;
  };
  // On screen only, folded: a reviewer drills in here; the PDF carries the 3
  // examples per check, and the evidence HAR carries every request.
  return `<section class="inventory-section no-print"><details class="fold"><summary><h2 style="display:inline">Requests graded</h2></summary>
    <p class="small muted">Every request of each family in this run. "Won by" is the Slots view's winner for that request's slot. A request is failed or warned only when a check that grades that family named it; the checks are listed in its Result cell.</p>
    ${fams.map(section).join('')}</details></section>`;
}

/* ── launch-redesign (T30): per-branch lines, Not covered, observed values, hints, waivers ──
 * Frontend twin of the redesign half of proxy-server/launch/reportMd.js. The HTML report below and the
 * Markdown strings exported here are built from ONE structure (branchBlocks), and the Markdown is pinned
 * equal to reportMd.js by launchReportParity.test.js over proxy-server/launch/fixtures/reportParityCorpus.json.
 * Every sentence is from launchWording.js (twin of launch/wording.js). Gated on rollup.mode being
 * `shadow` or `on`: with LAUNCH_REDESIGN=off (or a proxy that sends no `mode`) nothing here prints.
 * The inputs are the proxy's answer (rollup entries carry `redesign.units`); this file never recomputes
 * a unit status. Keep in lockstep with reportMd.js; never throws. */

const rdObj = (v) => v != null && typeof v === 'object' && !Array.isArray(v);
const rdStr = (v) => (v == null ? '' : String(v));
const rdLine = (v) => rdStr(v).replace(/\r\n|\r|\n/g, ' ');
const rdList = (v) => (Array.isArray(v) ? v : []);
const MAX_OBSERVED_VALUES = 10;

// twin of conformance/idClass.js + evidenceValues.js safeValue (pinned by launchReportParity.test.js)
const ID_CLASS_ALT = [
  'rdid', 'rd_?id', 'ppid', 'eo_?id', 'idfa', 'ifa', 'gaid', 'aaid', 'idfv', 'vcid', 'uid', 'did',
  'buyeruid', 'adid', 'advertising_?id', 'android_?id', 'device_?id', 'user_?id',
].join('|');
const ID_CLASS_RE = new RegExp(`(?:^|[^a-z])(${ID_CLASS_ALT})(?:[^a-z]|$)`, 'i');
const ID_Q = '(?:["\']|%(?:25)?(?:22|27))';
const ID_SEP = '(?:[=:]|%(?:25)?(?:3d|3a))';
// Value ends at & " ' whitespace , ; } ( ) < > [ ] or %26/%22/%27 (and double-encoded). '/' does NOT end it. Twin of
// proxy-server/conformance/idClass.js VAL; `ppid=abc(1)` leaves `(1)` clear (known low leak).
const ID_VAL = '((?:(?!%(?:25)?(?:26|22|27))[^&"\'\\s,;}()<>\\[\\]])+)';
const ID_CLASS_PAIR_SRC = `((?:^|[^a-z])(?:${ID_CLASS_ALT})(?:_expanded)?[0-9]*${ID_Q}?\\s*${ID_SEP}\\s*${ID_Q}?)${ID_VAL}`;
const EVIDENCE_VALUE_CAP = 200;

function maskIdValue(value) {
  const v = rdStr(value);
  if (v.length > 12) return `${v.slice(0, 8)}…${v.slice(-4)}`;
  return v === '' ? '' : '[masked]';
}
function showValue(raw) {
  if (typeof raw === 'string') return raw;
  if (Array.isArray(raw)) return raw.map((x) => (typeof x === 'object' && x !== null ? JSON.stringify(x) : String(x))).join(', ');
  if (raw && typeof raw === 'object') return JSON.stringify(raw);
  return String(raw);
}
/** Twin of evidenceValues.maskIdText: mask name=value pairs for ID-class names inside free text. */
export function launchMaskText(text) {
  if (typeof text !== 'string' || !text) return text;
  return text.replace(new RegExp(ID_CLASS_PAIR_SRC, 'gi'), (_m, head, val) => `${head}${maskIdValue(val)}`);
}

export function launchSafeValue(raw, param) {
  const s = showValue(raw);
  const masked = (typeof param === 'string' && param !== '' && ID_CLASS_RE.test(param))
    ? maskIdValue(s)
    : s.replace(new RegExp(ID_CLASS_PAIR_SRC, 'gi'), (_m, head, val) => `${head}${maskIdValue(val)}`);
  return masked.length > EVIDENCE_VALUE_CAP ? `${masked.slice(0, EVIDENCE_VALUE_CAP - 1)}…` : masked;
}

function stableJson(v) {
  if (Array.isArray(v)) return `[${v.map(stableJson).join(',')}]`;
  if (v && typeof v === 'object') {
    return `{${Object.keys(v).sort().filter((k) => v[k] !== undefined).map((k) => `${JSON.stringify(k)}:${stableJson(v[k])}`).join(',')}}`;
  }
  return JSON.stringify(v === undefined ? null : v);
}
const asWhere = (w) => (w == null ? [] : (Array.isArray(w) ? w : [w]));

// twin of launch/compileStructured.js compileScope (scope.from inheritance; predicates AND, root first)
function scopeIdOf(c, i) { return rdObj(c) && typeof c.id === 'string' && c.id.trim() ? c.id.trim() : `c${i + 1}`; }
function compileScopeTwin(criterion, criteria) {
  const empty = { where: [], target: {} };
  try {
    if (!rdObj(criterion)) return empty;
    const byId = new Map();
    rdList(criteria).forEach((c, i) => byId.set(scopeIdOf(c, i), c));
    const me = scopeIdOf(criterion, -1) === 'c0' ? null : scopeIdOf(criterion, -1);
    if (me) byId.set(me, criterion);
    const links = [criterion];
    const seenIds = new Set(me ? [me] : []);
    let cur = criterion;
    for (let guard = 0; guard < 100; guard += 1) {
      const from = rdObj(cur.scope) && typeof cur.scope.from === 'string' ? cur.scope.from.trim() : '';
      if (!from || seenIds.has(from) || !byId.has(from)) break;
      seenIds.add(from);
      cur = byId.get(from);
      links.push(cur);
    }
    links.reverse();
    const where = [];
    const seen = new Set();
    const target = {};
    for (const c of links) {
      const s = rdObj(c.scope) ? c.scope : {};
      for (const p of asWhere(s.where)) {
        if (!rdObj(p)) continue;
        const k = stableJson(p);
        if (!seen.has(k)) { seen.add(k); where.push(p); }
      }
      if (rdObj(s.target)) for (const k of Object.keys(s.target)) if (!(k in target)) target[k] = s.target[k];
    }
    return { where, target };
  } catch {
    return empty;
  }
}

// twin of launch/observe.js flagFields
function flagFieldsTwin(branches) {
  const seen = new Map();
  for (const b of rdList(branches)) {
    for (const p of rdList(b && b.when)) {
      if (rdObj(p) && p.source && p.param) seen.set(`${p.source}\u0000${p.param}`, { source: p.source, param: p.param });
    }
  }
  return [...seen.values()];
}

export function redesignOn(rollup) {
  return !!rollup && typeof rollup === 'object' && (rollup.mode === 'shadow' || rollup.mode === 'on');
}

function unitStatusText(status, unconfirmed) {
  if (status === 'not_covered') {
    return unconfirmed ? `${WORDING.notCoveredRecapture} (${WORDING.unconfirmedAnnotation})` : WORDING.notCoveredRecapture;
  }
  return WORDING.statusLabels[status] || rdStr(status);
}

/** E8: the verdict sentence while any unit is Not covered and unwaived. null when none (or `off`). */
export function notCoveredSentence(rollup) {
  try {
    if (!redesignOn(rollup) || !(rollup.uncoveredUnits > 0)) return null;
    const entries = rdList(rollup.criteria);
    const ids = new Set(rdList(rollup.uncoveredUnitList).map((u) => u && u.criterionId));
    const nums = [];
    entries.forEach((e, i) => { if (e && ids.has(e.criterionId)) nums.push(i + 1); });
    return fill(WORDING.overallNotCovered, { x: nums.length, N: entries.length, list: nums.join(', ') });
  } catch {
    return null;
  }
}

/** E21: "N branches not verified (waived)" for waived units of every kind. null when none (or `off`). */
export function waivedHeaderLine(rollup) {
  try {
    return redesignOn(rollup) && rollup.waivedCount > 0 ? fill(WORDING.waivedHeader, { n: rollup.waivedCount }) : null;
  } catch {
    return null;
  }
}

/** E27: the status cell of a criterion the legacy rule calls Met whose new status is Not covered. null otherwise. */
export function unconfirmedStatusCell(entry) {
  return entry && entry.redesign && entry.redesign.unconfirmed === true ? WORDING.shadowLabel : null;
}

function describePredicate(p) {
  if (!rdObj(p)) return '';
  const field = [p.source, p.param].filter((x) => x != null && x !== '').join(' ');
  let operand = '';
  if (p.pattern != null) operand = p.pattern;
  else if (p.value != null) operand = p.value;
  else if (Array.isArray(p.values)) operand = p.values.join('|');
  return [field, p.op, operand].filter((x) => x != null && x !== '').map(String).join(' ');
}
const describePredicates = (list) => rdList(list).map(describePredicate).filter(Boolean).join(' and ');

// E25: the criterion's scope predicate (inherited scope included) for the no-rows hint.
function scopeText(criterion, criteria) {
  try {
    const sc = compileScopeTwin(criterion, criteria);
    const parts = [];
    const t = rdObj(sc.target) ? sc.target : {};
    const types = Array.isArray(t.requestType) ? t.requestType : (t.requestType ? [t.requestType] : []);
    if (types.length) parts.push(`request type ${types.join(' or ')}`);
    const u = t.urlPattern;
    if (u && typeof u === 'object') parts.push(`URL ${rdStr(u.host)}${rdStr(u.path)}${u.query ? `?${u.query}` : ''}`);
    else if (typeof u === 'string' && u) parts.push(`URL ${u}`);
    for (const p of sc.where) {
      const d = describePredicate(p);
      if (d) parts.push(d);
    }
    return parts.join(' and ');
  } catch {
    return '';
  }
}

function unitDefinition(criterion, unit) {
  const c = rdObj(criterion) ? criterion : {};
  const branch = rdList(c.branches).find((b) => b && String(b.id) === unit.id);
  const variants = c.scope && Array.isArray(c.scope.variants) ? c.scope.variants : [];
  const variant = variants.find((v) => v && String(v.id) === unit.id);
  const def = branch || variant || null;
  return {
    label: def && typeof def.label === 'string' ? def.label : '',
    when: describePredicates(branch ? branch.when : (variant ? variant.where : [])),
  };
}

function hintLines(unit, criterion, criteria, results) {
  const out = [];
  try {
    if (unit.kind === 'observe') {
      for (const item of rdList(unit.unread)) out.push(fill(WORDING.hints.observe_unread, { item }));
      return out;
    }
    let code = unit.reason === 'no_rows_matched' ? 'no_rows_matched' : null;
    if (!code) {
      for (const cid of rdList(unit.checkIds)) {
        const r = effectiveNaReason(rdObj(results) ? results[cid] || null : null);
        if (COVERAGE_GAP_REASONS.includes(r)) { code = r; break; }
      }
    }
    if (!code) return out;
    const hint = captureGapHint(code, { scope: scopeText(criterion, criteria), when: unitDefinition(criterion, unit).when });
    if (hint) out.push(hint);
  } catch { /* a hint is additive */ }
  return out;
}

function observedLines(unit, criterion, observedById, results) {
  const out = [];
  try {
    if (unit.kind === 'observe') {
      const o = rdObj(observedById) ? observedById[criterion.id] : null;
      for (const it of (o && Array.isArray(o.items) ? o.items : [])) {
        const name = rdStr(it.label || it.param);
        if (!it.read) {
          if (it.unreadable > 0) out.push(fill(WORDING.observedUnreadable, { param: name }));
          continue;
        }
        const vals = Array.isArray(it.values) ? it.values : [];
        for (const v of vals.slice(0, MAX_OBSERVED_VALUES)) {
          if (it.kind === 'presence') {
            out.push(v.value === 'absent' ? fill(WORDING.observedAbsent, { param: name }) : fill(WORDING.observedValue, { param: name, value: rdStr(v.value) }));
          } else {
            out.push(fill(WORDING.observedValue, { param: name, value: v.empty ? '(empty)' : launchSafeValue(v.value, it.param) }));
          }
        }
        if (vals.length > MAX_OBSERVED_VALUES) out.push(`+${vals.length - MAX_OBSERVED_VALUES} more ${name} values not shown`);
      }
      return out;
    }
    const seen = new Set();
    for (const cid of rdList(unit.checkIds)) {
      const res = rdObj(results) ? results[cid] : null;
      for (const f of (res && Array.isArray(res.findings) ? res.findings : [])) {
        for (const v of (f && Array.isArray(f.values) ? f.values : [])) {
          if (!v || v.role !== 'assert') continue;
          const param = rdStr(v.param || v.source);
          let line;
          if (v.state === 'read') line = fill(WORDING.observedValue, { param, value: v.empty ? '(empty)' : rdStr(v.value) });
          else if (v.state === 'absent') line = fill(WORDING.observedAbsent, { param });
          else line = fill(WORDING.observedUnreadable, { param });
          if (!seen.has(line) && seen.size < MAX_OBSERVED_VALUES) { seen.add(line); out.push(line); }
        }
      }
    }
  } catch { /* observed values are additive */ }
  return out;
}

function waiverFor(waivers, criterionId, unitId) {
  const list = rdList(waivers);
  for (let i = list.length - 1; i >= 0; i -= 1) {
    const w = list[i];
    if (w && w.criterionId === criterionId && w.branchId === unitId) return w;
  }
  return null;
}

function unitLineText(unit, criterion, label) {
  if (unit.id === NO_BRANCH_UNIT) {
    const f = flagFieldsTwin(criterion.branches)[0];
    return fill(WORDING.noBranchApplies, { field: f ? f.param : '' });
  }
  const status = unitStatusText(unit.status, unit.unconfirmed === true);
  if (unit.status === 'met' && typeof unit.graded === 'number') {
    return fill(WORDING.branchLineGraded, { label, status, graded: unit.graded, unreadable: Number(unit.unreadable) || 0 });
  }
  return fill(WORDING.branchLine, { label, status });
}

function qualifiesForBranchBlock(rd, criterion) {
  const list = rdList(rd.units);
  const interp = criterion.interpretedAs && criterion.interpretedAs.field;
  return !!interp || list.some((u) => u.kind !== 'default' || u.status === 'not_covered' || u.status === 'waived');
}

/**
 * branchBlocks(rollup, ctx) -> [{ id, text, status, interpreted, units:[{ line, subs:[] }] }] | null.
 * ctx: { criteria, observedById, waivers, resultsByCheckId } (resultsByCheckId a plain object).
 * Texts are already newline-collapsed exactly as reportMd.js collapses them.
 */
export function branchBlocks(rollup, ctx) {
  try {
    if (!redesignOn(rollup)) return null;
    const x = rdObj(ctx) ? ctx : {};
    const entries = rdList(rollup.criteria);
    const criteria = rdList(x.criteria);
    const byId = new Map();
    for (const c of criteria) if (c && c.id != null) byId.set(c.id, c);
    const blocks = [];
    for (const e of entries) {
      const rd = e && e.redesign;
      if (!rd || !Array.isArray(rd.units)) continue;
      const c = byId.get(e.criterionId) || { id: e.criterionId, text: e.text };
      if (!qualifiesForBranchBlock(rd, c)) continue;
      const block = {
        id: rdLine(e.criterionId) || '?',
        text: rdLine(e.text) || '(empty criterion)',
        status: rd.unconfirmed === true ? WORDING.shadowLabel : unitStatusText(rd.status, false),
        interpreted: rdObj(c.interpretedAs) && c.interpretedAs.field
          ? fill(WORDING.interpretedAs, { text: rdStr(c.interpretedAs.acText), field: rdStr(c.interpretedAs.field) })
          : null,
        units: [],
      };
      for (const u of rd.units) {
        const label = rdLine(unitDefinition(c, u).label) || rdLine(e.text) || rdStr(u.id);
        const subs = [];
        const w = u.status === 'waived' ? waiverFor(x.waivers, e.criterionId, u.id) : null;
        if (w) subs.push(rdLine(fill(WORDING.waivedLine, { signer: rdStr(w.by), time: rdStr(w.at), reason: launchMaskText(rdStr(w.reason)) })));
        for (const l of observedLines(u, c, x.observedById, x.resultsByCheckId)) subs.push(rdLine(l));
        if (u.status === 'not_covered') for (const l of hintLines(u, c, criteria, x.resultsByCheckId)) subs.push(rdLine(l));
        block.units.push({ line: rdLine(unitLineText(u, c, label)), subs });
      }
      blocks.push(block);
    }
    return blocks.length ? blocks : null;
  } catch {
    return null;
  }
}

/** The "## Branch results" Markdown section, character-equal to reportMd.js's. null when there is none. */
export function branchResultsMd(rollup, ctx) {
  const blocks = branchBlocks(rollup, ctx);
  if (!blocks) return null;
  const text = blocks.map((b) => {
    const lines = [`### ${b.id} — ${b.text}`, '', `Status: **${b.status}**`];
    if (b.interpreted) lines.push('', b.interpreted);
    lines.push('');
    for (const u of b.units) {
      lines.push(`- ${u.line}`);
      for (const s of u.subs) lines.push(`  - ${s}`);
    }
    return lines.join('\n');
  }).join('\n\n');
  return ['## Branch results', '', WORDING.notMetDefinition, '', WORDING.notCoveredDefinition, '', text].join('\n');
}

function branchResultsHtml(rollup, ctx) {
  const blocks = branchBlocks(rollup, ctx);
  if (!blocks) return '';
  const body = blocks.map((b) => `
      <div class="crit-body">
        <p><b>${esc(b.id)} — ${esc(b.text)}</b></p>
        <div class="small">Status: <b>${esc(b.status)}</b></div>
        ${b.interpreted ? `<div class="small">${esc(b.interpreted)}</div>` : ''}
        <ul class="checks">${b.units.map((u) => `<li>${esc(u.line)}${u.subs.length ? `<ul>${u.subs.map((s) => `<li class="small">${esc(s)}</li>`).join('')}</ul>` : ''}</li>`).join('')}</ul>
      </div>`).join('');
  return `<div class="section branch-results"><div class="section-title">Branch results</div>
      <p class="section-lead">${esc(WORDING.notMetDefinition)}<br>${esc(WORDING.notCoveredDefinition)}</p>${body}</div>`;
}

const plainResults = (r) => {
  try {
    if (!r) return {};
    if (typeof r.entries === 'function' && typeof r.get === 'function') return Object.fromEntries(r.entries());
    return typeof r === 'object' ? r : {};
  } catch { return {}; }
};

export function buildLaunchReport(launch, resultsByCheckId, { tester = '', runId = '', app = '', checkDefsById = null, capture = null, rows = null, winners = null, serverRollup = null, sdkBiddingFallback = null, observedById = null } = {}) {
  try {
    return buildLaunchReportInner(launch, resultsByCheckId, { tester, runId, app, checkDefsById, capture, rows, winners, serverRollup, sdkBiddingFallback, observedById });
  } catch (e) {
    const msg = esc(e?.message || String(e) || 'unknown error');
    return `<!doctype html><html><head><meta charset="UTF-8"><title>Launch sign-off — error</title></head>
<body style="font-family:-apple-system,sans-serif;padding:32px;color:#8a1a1a">
<h1>Could not generate this report</h1>
<p>${msg}</p>
</body></html>`;
  }
}

// ── human wording for how a criterion was graded (replaces the old
// "Existing check / Authored check / Manual" chips — those were the
// compiler's internal mapping vocabulary, meaningless to a PM or vendor
// reading the signed document) ────────────────────────────────────────────

function gradedByLabel(c, checkCount) {
  const m = c?.mapping || 'unmapped';
  if (m === 'manual') return 'Manual verification — not provable from network traffic';
  if (m === 'unmapped' || checkCount === 0) return 'No check mapped';
  const n = `${checkCount} check${checkCount === 1 ? '' : 's'}`;
  return m === 'authored' ? `Graded by ${n} authored for this launch` : `Graded by ${n} from the standing suite`;
}

const TONE_STYLE = {
  fail: { color: '#8a1a1a', bg: '#fff0f0' },
  warn: { color: '#854f0b', bg: '#fffbea' },
  info: { color: '#1c5f8a', bg: '#eef6fc' },
  pass: { color: '#1a6f3c', bg: '#edfaf3' },
  muted: { color: '#555555', bg: '#f5f5f5' },
};

function fmtCaptureAt(at) {
  if (!at) return '';
  try {
    const d = new Date(at);
    if (Number.isNaN(d.getTime())) return String(at);
    const { dateStr, timeStr } = fmtDateTime(d);
    return `${dateStr} · ${timeStr}`;
  } catch { return String(at); }
}

function buildLaunchReportInner(launch, resultsByCheckId, { tester, runId, app, checkDefsById, capture, rows, winners, serverRollup, sdkBiddingFallback, observedById }) {
  const now = new Date();
  const { dateStr, timeStr } = fmtDateTime(now);

  const L = launch && typeof launch === 'object' ? launch : {};
  const criteria = Array.isArray(L.criteria) ? L.criteria : [];
  const appLabel = app || L.app || '';
  const cap = capture && typeof capture === 'object' ? capture : null;

  // Run id(s): launch.runIds newest-first, plus the passed runId (surfaced
  // first — it's the run this report was generated against), deduped.
  const historicalRunIds = Array.isArray(L.runIds) ? [...L.runIds].reverse() : [];
  const runIdsList = uniq([runId, ...historicalRunIds]);

  // ── verdict rollup ──
  // The app's launch header shows the PROXY's rollup (GET /proxy/launches/<id>/rollup,
  // folded across recorded runs, with the low-confidence downgrade). The report
  // must print the same verdicts, so a criterion's status comes from that rollup
  // whenever the caller passes it; the local rollup is only the fallback.
  const serverEntries = new Map(
    (serverRollup && Array.isArray(serverRollup.criteria) ? serverRollup.criteria : [])
      .filter((e) => e && e.criterionId != null)
      .map((e) => [e.criterionId, e]),
  );
  const statusOf = (c) => {
    const e = c && serverEntries.get(c.id);
    return e && typeof e.status === 'string' ? e.status : criterionStatus(c, resultsByCheckId);
  };
  const downgradeOf = (c) => {
    const e = c && serverEntries.get(c.id);
    return e && e.downgraded === 'low_confidence' ? e : null;
  };
  const rollup = { met: 0, notMet: 0, needsReview: 0, manualReview: 0, notRun: 0 };
  for (const c of criteria) {
    const st = statusOf(c);
    if (st === 'met') rollup.met += 1;
    else if (st === 'not-met') rollup.notMet += 1;
    else if (st === 'needs-review') rollup.needsReview += 1;
    else if (st === 'manual-review') rollup.manualReview += 1;
    else rollup.notRun += 1;
  }
  // Wording per docs/signoff-report-template.md §2 — one line, first match wins.
  const partlyVerifiedCount = criteria.filter((c) => Array.isArray(c?.coverage?.missing) && c.coverage.missing.length > 0).length;
  const ov = overallVerdict(
    criteria.map((c, i) => ({ n: i + 1, text: c?.text, status: statusOf(c) })),
    { coverageIncomplete: partlyVerifiedCount > 0 },
  );
  const overall = ov.headline;
  const overallTone = ov.tone;
  const verdictMeaning = ov.line;
  const overallColor = TONE_STYLE[overallTone].color;
  const overallBg = TONE_STYLE[overallTone].bg;

  // ── what stands between this report and a signature ──
  const partlyVerified = criteria.filter((c) => Array.isArray(c?.coverage?.missing) && c.coverage.missing.length > 0).length;
  const plural = (n, one, many) => (n === 1 ? one : many);
  const todo = [];
  if (rollup.notMet > 0) todo.push({ tone: 'fail', head: `${rollup.notMet} not met`, text: `A mapped check failed on real requests. Fix the build${plural(rollup.notMet, '', 's')} and re-run the checks; the evidence links in each card show the exact requests.` });
  if (rollup.needsReview > 0) todo.push({ tone: 'warn', head: `${rollup.needsReview} need${plural(rollup.needsReview, 's', '')} review`, text: 'A check warned or only partly covered the criterion. Review the evidence and acknowledge it below if acceptable.' });
  if (rollup.manualReview > 0) todo.push({ tone: 'info', head: `${rollup.manualReview} manual`, text: 'These cannot be proven from network traffic. Verify each one as described in its card, then acknowledge it below.' });
  if (rollup.notRun > 0) todo.push({ tone: 'muted', head: `${rollup.notRun} not run`, text: 'No check result exists for these criteria. Each card states why (no check mapped, or its checks were not part of this run) and what would settle it.' });
  if (partlyVerified > 0) todo.push({ tone: 'warn', head: `${partlyVerified} partly verified`, text: 'The criterion names traffic that nothing graded. Treat the verdict as covering only the traffic it names.' });

  // launch-redesign (T30): only when the proxy's rollup says `shadow` or `on`; otherwise the report is unchanged.
  const rdOn = redesignOn(serverRollup);
  const rdCtx = { criteria, observedById, waivers: L.waivers, resultsByCheckId: plainResults(resultsByCheckId) };
  const notCoveredLine = rdOn ? notCoveredSentence(serverRollup) : null;
  if (notCoveredLine) todo.push({ tone: 'warn', head: 'Not covered', text: notCoveredLine });
  const waivedLine = rdOn ? waivedHeaderLine(serverRollup) : null;
  const branchHtml = rdOn ? branchResultsHtml(serverRollup, rdCtx) : '';

  const todoHtml = todo.length === 0
    ? ''
    : `<div class="section">
         <div class="section-title">Before this can be signed</div>
         <ul class="todo">${todo.map((t) => `
           <li><span class="pill" style="color:${TONE_STYLE[t.tone].color};background:${TONE_STYLE[t.tone].bg};border-color:${TONE_STYLE[t.tone].color}40">${esc(t.head)}</span><span>${esc(t.text)}</span></li>`).join('')}
         </ul>
       </div>`;

  // ── one card per criterion ──
  const cardsHtml = criteria.map((c, idx) => {
    const status = statusOf(c);
    const checks = criterionResults(c, resultsByCheckId);
    const evidence = pickEvidence(checks);
    const mappedIds = Array.isArray(c?.checkIds) ? c.checkIds.filter(Boolean) : [];
    const isManual = (c?.mapping === 'manual') || status === CRITERION_STATUS.MANUAL_REVIEW;

    const coverageHtml = (Array.isArray(c?.coverage?.missing) && c.coverage.missing.length)
      ? `<div class="callout warn"><b>Partly verified</b> — nothing checked ${esc(c.coverage.missing.join(', '))} traffic, which this criterion names.</div>`
      : '';
    const rationaleHtml = c?.rationale ? `<p class="why">${esc(c.rationale)}</p>` : '';
    const noteHtml = c?.manualNote
      ? `<div class="callout info"><div class="callout-label">${isManual ? 'How to verify' : 'Tester note'}</div>${esc(c.manualNote)}</div>`
      : '';

    // Mapped checks: id, outcome, counts, and ONE line of what it grades.
    // The full assertion/scope for every check lives under "Checks tested"
    // — the card is for reading the verdict, not auditing the suite. Checks
    // that failed or warned are always visible; a long tail of passed /
    // not-run checks (a baseline suite can map 20 to one criterion) folds
    // behind a toggle so the card stays readable. Print expands it.
    const checkItem = ({ checkId, result }) => {
      const matched = typeof result?.matched === 'number' ? result.matched : null;
      const found = typeof result?.found === 'number' ? result.found : null;
      const counts = matched != null ? `<span class="counts">matched ${matched}${found != null ? ` · found ${found}` : ''}</span>` : '';
      const trunc = result?.rowsTruncated ? '<div class="small warn-text">evidence truncated upstream at 50 rows</div>' : '';
      const note = result?.note ? `<div class="small">${esc(result.note)}</div>` : '';
      // One plain sentence: what was looked at, which value was read from where,
      // how many readable requests held, and one example (utils/evidenceLine.js;
      // twin of the proxy Markdown report's). Built from THIS check's own result.
      const sentence = evidenceLine(getCheckDef(checkDefsById, checkId), result ?? null);
      // masked at print time: the sentence quotes a read value, clear if an older proxy sent it unmasked
      const lineHtml = `<div class="evidence-line">${esc(launchMaskText(sentence))}</div>`;
      const sentenceHtml = `<div class="check-sentence"><b>${esc(checkSentence(getCheckDef(checkDefsById, checkId), checkId, result ?? null, c))}</b></div>`;
      return `<li>${sentenceHtml}<div class="check-line"><code>${esc(checkId)}</code> ${checkStatusChip(result?.status)}${counts}</div>${checkDetailLine(checkId, checkDefsById)}${lineHtml}${proofRowsHtml(result, getCheckDef(checkDefsById, checkId))}${note}${trunc}</li>`;
    };
    const isAttention = ({ result }) => result?.status === 'fail' || result?.status === 'warn';
    const attention = checks.filter(isAttention);
    const rest = checks.filter((x) => !isAttention(x));
    const FOLD_AT = 4;
    let checksHtml = '';
    if (checks.length) {
      const tally = { pass: 0, fail: 0, warn: 0, other: 0 };
      for (const { result } of checks) {
        const st = result?.status;
        if (st === 'pass') tally.pass += 1; else if (st === 'fail') tally.fail += 1; else if (st === 'warn') tally.warn += 1; else tally.other += 1;
      }
      const tallyBits = [];
      if (tally.fail) tallyBits.push(`${tally.fail} failed`);
      if (tally.warn) tallyBits.push(`${tally.warn} warned`);
      if (tally.pass) tallyBits.push(`${tally.pass} passed`);
      if (tally.other) tallyBits.push(`${tally.other} not run`);
      const listHtml = (items) => `<ul class="checks">${items.map(checkItem).join('')}</ul>`;
      const foldRest = rest.length > FOLD_AT || (attention.length > 0 && rest.length > 0);
      checksHtml = `<div class="label">${esc(gradedByLabel(c, checks.length))}<span class="label-tail"> · ${esc(tallyBits.join(', '))}</span></div>`
        + (attention.length ? listHtml(attention) : '')
        + (rest.length
          ? (foldRest
            ? `<details class="fold"><summary>${attention.length ? 'Show' : 'Show all'} ${rest.length} ${attention.length ? 'other ' : ''}check${rest.length === 1 ? '' : 's'}</summary>${listHtml(rest)}</details>`
            : listHtml(rest))
          : '');
    } else if (!isManual) {
      checksHtml = `<div class="label">${esc(gradedByLabel(c, mappedIds.length))}</div>`
        + `<div class="evidence-line">${esc(unmappedEvidenceLine())}</div>`;
    }
    // Manual cards say so in the same voice as every other card, beneath
    // "How to verify" (the tester's note) when there is one.
    const manualLineHtml = isManual && !floorDowngrade(c) ? `<div class="evidence-line">${esc(manualEvidenceLine())}</div>` : '';

    // Template §3: verdict sentence, scope, then what would settle it.
    const openOne = checks.find(({ result }) => !result || (result.status !== 'pass' && result.status !== 'fail')) || checks[0] || null;
    const openDef = openOne ? getCheckDef(checkDefsById, openOne.checkId) : null;
    const dg = downgradeOf(c);
    const critSentence = dg
      ? `Needs review. Every mapped check passed, but the compiler's confidence that these checks prove this criterion is ${Math.round((dg.confidence || 0) * 100)}%, below the 70% sign-off floor.`
      : ((status === CRITERION_STATUS.NEEDS_REVIEW && mixedNeedsReview(checks, c, (id) => getCheckDef(checkDefsById, id)))
        || criterionSentence(status, c, openOne ? (openOne.result ?? null) : null, openDef));
    const critSentenceHtml = `<p class="crit-verdict"><b>${esc(critSentence)}</b></p>`;
    const scope = scopeLine(checks.map((x) => x.result).filter(Boolean), { platform: cap?.platform, captureLabel: cap?.label });
    const scopeHtml = scope ? `<div class="small">${esc(scope)}</div>` : '';
    const confirmHtml = dg
      ? '<div class="small"><b>To confirm: read each check below and acknowledge that it proves this criterion, or reword the criterion and re-compile.</b></div>'
      : (status === CRITERION_STATUS.NEEDS_REVIEW || status === CRITERION_STATUS.NOT_RUN)
        ? (() => { const r = reasonFor(c, openOne ? (openOne.result ?? null) : null, openDef); return `<div class="small"><b>${esc(toConfirmLine(openDef, r?.code, r?.text))}</b></div>`; })() : '';
    const firstFail = checks.find(({ result }) => result?.status === 'fail');
    const failDef = firstFail ? getCheckDef(checkDefsById, firstFail.checkId) : null;
    const fixHtml = (status === CRITERION_STATUS.NOT_MET && failDef?.fix)
      ? `<div class="small"><b>Fix:</b> ${esc(failDef.fix)}</div>` : '';
    const manualConfirmHtml = isManual
      ? `<div class="small">Confirmed by: ${esc(c?.confirmedBy || 'not provided')} on ${esc(c?.confirmedAt || 'not provided')} via ${esc(c?.confirmMethod || 'not provided')}.</div>
         <div class="small">Evidence: ${esc(c?.confirmEvidence || 'not provided')}.</div>`
      : '';

    // Every check above already shows its own example requests, so the pooled
    // list is folded (and stays folded in print) instead of repeating them.
    const evidenceHtml = evidence.rows.length
      ? `<details class="fold evidence-all"><summary>All evidence requests for this criterion (${evidence.rows.length})</summary>
         <div class="evidence">${evidence.rows.map((r) => evidenceRowHtml(r)).join('')}${evidenceCapNotesHtml(evidence)}</div></details>`
      : (checks.length ? `<div class="small muted">No evidence rows were retained for this criterion.</div>${evidenceCapNotesHtml(evidence)}` : '');

    return `
      <section class="crit" id="crit-${idx + 1}">
        <div class="crit-head">
          <div class="crit-num">${idx + 1}</div>
          <div class="crit-text">${esc(c?.text || '(empty criterion)')}</div>
          <div class="crit-status">${statusChip(status)}${unconfirmedStatusCell(serverEntries.get(c?.id)) ? `<div class="small">${esc(unconfirmedStatusCell(serverEntries.get(c?.id)))}</div>` : ''}</div>
        </div>
        <div class="crit-body">
          ${critSentenceHtml}
          ${coverageHtml}
          ${rationaleHtml}
          ${noteHtml}
          ${manualLineHtml}
          ${manualConfirmHtml}
          ${checksHtml}
          ${fixHtml}
          ${confirmHtml}
          ${scopeHtml}
          ${evidenceHtml}
        </div>
      </section>`;
  }).join('');

  const criteriaSectionHtml = criteria.length === 0
    ? '<p class="empty">No criteria have been compiled for this launch yet — paste acceptance criteria and compile before generating a sign-off report.</p>'
    : `<div class="section"><div class="section-title">Acceptance criteria — ${criteria.length}</div>${cardsHtml}</div>`;

  // ── Observations (template §4) and Limits of this capture (§5) ──
  const obs = observations(rows);
  const observationsHtml = obs.length
    ? `<div class="section"><div class="section-title">Observations (not criteria)</div>
         <p class="section-lead">Findings in the captured traffic that no criterion asked about. They do not change any verdict.</p>
         <ul class="todo">${obs.map((o) => `<li><span class="pill" style="color:${TONE_STYLE[o.severity === 'fail' ? 'fail' : 'warn'].color};background:${TONE_STYLE[o.severity === 'fail' ? 'fail' : 'warn'].bg}">${esc(o.severity)}</span><span>${esc(o.what)} · ${o.count} · ${esc(o.at || 'time unknown')}</span></li>`).join('')}</ul>
       </div>` : '';
  // `serverRollup.scope` (proxy P13) carries excludedUnownedOwnRows / mixedOwners; older proxies omit it.
  const limits = limitsOfCapture(rows, { scope: serverRollup && serverRollup.scope });
  const limitsHtml = limits.length
    ? `<div class="section"><div class="section-title">Limits of this capture</div><ul>${limits.map((l) => `<li class="small">${esc(l)}</li>`).join('')}</ul></div>` : '';
  // SDK bidding (docs/sdlc/sdk-bidding-signoff): the proxy's rollup `sdkBidding` payload wins and is
  // drawn only when its `mode` is 'on'. The browser copy runs only if the caller opts in
  // (`sdkBiddingFallback: 'on'|'shadow'`) and no payload came back. Advisory: touches no verdict.
  const sdkBiddingHtml = sdkBiddingSectionFor({
    payload: serverRollup && serverRollup.sdkBidding, rows, fallbackMode: sdkBiddingFallback,
  });

  // ── acknowledgement section: one checkbox per manual-review / needs-review
  // / not-run criterion. Sign-off rule: zero not-met AND every checkbox
  // acked. ──
  const ackCriteria = criteria.filter((c) => {
    const s = statusOf(c);
    return s === CRITERION_STATUS.MANUAL_REVIEW || s === CRITERION_STATUS.NEEDS_REVIEW || s === CRITERION_STATUS.NOT_RUN;
  });
  const ackItemsHtml = ackCriteria.map((c, i) => {
    const s = statusOf(c);
    const n = criteria.indexOf(c) + 1;
    return `
      <li>
        <input type="checkbox" class="ack-check" id="ack${i}">
        <label for="ack${i}">${statusChip(s)} <span class="ack-num">${n}.</span> ${esc(c?.text || '(empty criterion)')}</label>
      </li>`;
  }).join('');

  const ackSectionHtml = ackCriteria.length === 0
    ? ''
    : `<div class="section">
         <div class="section-title">Acknowledge before sign-off</div>
         <p class="section-lead">Tick each item once it has been reviewed or verified outside the tool. The signature block unlocks when every item is ticked and no criterion is not met.</p>
         <ul class="ack">${ackItemsHtml}</ul>
       </div>`;

  const hardFails = rollup.notMet;
  const ackTotal = ackCriteria.length;

  const signoffBlockHtml = `
    <div class="section">
      <div class="section-title">Sign-off</div>
      <div id="signoff-gate" class="gate warn" style="display:none"></div>
      <div id="signoff-blocked" class="gate fail" style="display:none">Cannot sign off — ${hardFails} not-met criteri${hardFails === 1 ? 'on' : 'a'} must be resolved first.</div>
      <div id="signoff-stamp" class="stamp" style="display:none">
        <div class="stamp-title">Signed off</div>
        <div class="stamp-meta">Tester: <b>${esc(tester) || '________________'}</b> · ${esc(dateStr)} at ${esc(timeStr)}</div>
      </div>
      <div class="sig-grid">
        <div class="sig"><div class="sig-line"></div><div class="sig-label">Name</div></div>
        <div class="sig"><div class="sig-line"></div><div class="sig-label">Role</div></div>
        <div class="sig"><div class="sig-line"></div><div class="sig-label">Date</div></div>
      </div>
    </div>`;

  // ── "Checks tested" — the auditable list this whole feature exists for.
  // One entry per DISTINCT check across the whole launch (a check shared by
  // two criteria is listed once), showing id, description, scope, the full
  // assertion, and severity — everything a signer needs to answer "why does
  // this criterion read Met" without going back to the suite JSON. Sits next
  // to the appendix (raw pass/fail results): this section is the SPEC each
  // check embodies, the appendix is the OUTCOME of running it.
  const allCheckIds = uniq(criteria.flatMap((c) => (Array.isArray(c?.checkIds) ? c.checkIds.filter(Boolean) : [])));
  const checksTestedRowsHtml = allCheckIds.map((checkId) => {
    const def = getCheckDef(checkDefsById, checkId);
    if (!def) {
      return `
        <div class="def">
          <div class="def-id"><code>${esc(checkId)}</code></div>
          <div class="small warn-text"><i>${esc(DEF_UNAVAILABLE_NOTE)}</i></div>
        </div>`;
    }
    const d = describeCheck(def);
    const description = d.description && d.description.trim() ? d.description.trim() : d.rule;
    return `
      <div class="def">
        <div class="def-id"><code>${esc(d.id)}</code><span class="sev">${esc(d.severity)}</span></div>
        ${d.statement ? `<div class="def-stmt">${esc(d.statement)}</div>` : ''}
        <div class="def-desc">${esc(description)}</div>
        ${d.why ? `<div class="def-kv"><b>Why it matters:</b> ${esc(d.why)}</div>` : ''}
        ${d.fix ? `<div class="def-kv"><b>If it fails:</b> ${esc(d.fix)}</div>` : ''}
        <div class="def-kv"><b>Scope:</b> ${esc(d.scope)}</div>
        <div class="def-kv"><b>Assertion:</b> ${esc(d.rule)}</div>
      </div>`;
  }).join('');

  const checksTestedHtml = allCheckIds.length === 0
    ? ''
    : `<details class="section" open>
         <summary class="section-title">Checks tested — ${allCheckIds.length}</summary>
         <p class="section-lead">Every distinct check mapped to a criterion in this launch and exactly what it asserts. One entry per check id, even where several criteria share a check.</p>
         ${checksTestedRowsHtml}
       </details>`;

  // ── appendix: raw check results ──
  const entries = resultEntries(resultsByCheckId);
  const appendixRowsHtml = entries.map(([checkId, result]) => {
    const matched = typeof result?.matched === 'number' ? result.matched : '—';
    const found = typeof result?.found === 'number' ? result.found : '—';
    return `
      <tr>
        <td><code>${esc(checkId)}</code></td>
        <td class="c">${checkStatusChip(result?.status)}</td>
        <td class="c">${esc(String(matched))}</td>
        <td class="c">${esc(String(found))}</td>
        <td class="small">${esc(result?.note || '')}</td>
      </tr>`;
  }).join('');

  const appendixHtml = entries.length === 0
    ? ''
    : `<details class="section">
         <summary class="section-title">Appendix — raw check results</summary>
         <table>
           <thead><tr><th>Check id</th><th class="c">Status</th><th class="c">Matched</th><th class="c">Found</th><th>Note</th></tr></thead>
           <tbody>${appendixRowsHtml}</tbody>
         </table>
       </details>`;

  const runIdsHtml = runIdsList.length
    ? runIdsList.map((id) => `<code>${esc(id)}</code>`).join(', ')
    : '<span class="muted">none yet</span>';

  // Capture context — which traffic this verdict is about. Omitted entirely
  // when the caller passes nothing, never invented.
  const captureBits = [];
  if (cap) {
    if (cap.label) captureBits.push(`<div class="kv"><div class="k">Source</div><div class="v">${esc(cap.label)}${cap.at ? ` <span class="muted">· ${esc(fmtCaptureAt(cap.at))}</span>` : ''}</div></div>`);
    if (cap.scope) captureBits.push(`<div class="kv"><div class="k">Scope</div><div class="v">${esc(cap.scope)}</div></div>`);
    if (cap.device) captureBits.push(`<div class="kv"><div class="k">Device</div><div class="v">${esc(cap.device)}</div></div>`);
  }
  // Template §1 fields from the graded rows; each prints its fixed empty value.
  const cf = captureFacts(rows);
  captureBits.push(`<div class="kv"><div class="k">Platform</div><div class="v">${esc(cap?.platform || cf.platform || 'not scoped')}</div></div>`);
  captureBits.push(`<div class="kv"><div class="k">Capture window</div><div class="v">${esc(cf.window || 'no requests graded')}</div></div>`);
  captureBits.push(`<div class="kv"><div class="k">Requests in scope</div><div class="v">${esc(cf.requests || '0')}</div></div>`);
  if (cf.testMode) captureBits.push(`<div class="kv"><div class="k">Test mode</div><div class="v">${esc(cf.testMode)}</div></div>`);

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>Launch sign-off — ${esc(L.title || 'Untitled launch')}</title>
<style>
  * { box-sizing: border-box; }
  html { -webkit-text-size-adjust: 100%; }
  body { margin: 0 auto; padding: 40px 32px 56px; max-width: 920px; font: 14px/1.5 -apple-system, BlinkMacSystemFont, 'Segoe UI', Inter, Roboto, sans-serif; color: #1b1b1f; background: #fff; }
  code { font: 12px/1.4 ui-monospace, Menlo, Consolas, monospace; background: #f4f4f6; padding: 1px 5px; border-radius: 4px; }
  a { color: #1c5f8a; text-decoration: none; }
  a:hover { text-decoration: underline; }
  .muted { color: #6b6b73; }
  .small { font-size: 12px; color: #55555d; }
  .warn-text { color: #854f0b; }
  .c { text-align: center; }

  .top { display: flex; justify-content: space-between; align-items: flex-start; gap: 24px; }
  .eyebrow { font-size: 11px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: #6b6b73; margin-bottom: 6px; }
  h1 { font-size: 26px; line-height: 1.2; font-weight: 800; margin: 0 0 16px; letter-spacing: -0.01em; }
  .meta-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(190px, 1fr)); gap: 10px 24px; margin-bottom: 24px; }
  .kv .k { font-size: 11px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: #6b6b73; }
  .kv .v { font-size: 13px; margin-top: 1px; word-break: break-word; }
  .print-btn { flex-shrink: 0; padding: 8px 16px; background: #1b1b1f; color: #fff; border: 0; border-radius: 6px; font-size: 13px; font-weight: 600; cursor: pointer; }

  .verdict-box { display: grid; grid-template-columns: auto 1fr; gap: 6px 24px; align-items: center; padding: 18px 22px; border-radius: 10px; border: 1px solid ${overallColor}40; background: ${overallBg}; margin: 0 0 20px; }
  .verdict { font-size: 28px; font-weight: 900; letter-spacing: -0.01em; color: ${overallColor}; }
  .verdict-meaning { font-size: 14px; color: #2a2a30; }
  .verdict-counts { grid-column: 1 / -1; display: flex; flex-wrap: wrap; gap: 8px; margin-top: 4px; }
  .pill { display: inline-block; padding: 2px 10px; border-radius: 999px; font-size: 12px; font-weight: 700; border: 1px solid transparent; white-space: nowrap; }

  .section { margin: 28px 0 0; }
  .section-title { font-size: 13px; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase; color: #3a3a42; border-bottom: 1px solid #e3e3e8; padding-bottom: 6px; margin-bottom: 12px; }
  details.section > summary { cursor: pointer; list-style: none; }
  details.section > summary::-webkit-details-marker { display: none; }
  details.section > summary::before { content: '▸'; display: inline-block; width: 14px; color: #8a8a93; transition: transform .15s; }
  details.section[open] > summary::before { transform: rotate(90deg); }
  .section-lead { font-size: 13px; color: #55555d; margin: 0 0 12px; }
  .empty { font-size: 13px; color: #55555d; padding: 16px; border: 1px dashed #c9c9d1; border-radius: 8px; }

  .todo { list-style: none; margin: 0; padding: 0; display: grid; gap: 8px; }
  .todo li { display: grid; grid-template-columns: auto 1fr; gap: 12px; align-items: baseline; font-size: 13px; }

  .crit { border: 1px solid #e3e3e8; border-radius: 10px; margin-bottom: 14px; }
  .crit-head { display: grid; grid-template-columns: 34px 1fr auto; gap: 12px; align-items: start; padding: 14px 16px; background: #fafafb; border-bottom: 1px solid #ececf0; border-radius: 10px 10px 0 0; }
  .crit-num { width: 28px; height: 28px; border-radius: 50%; background: #1b1b1f; color: #fff; font-weight: 800; font-size: 13px; display: flex; align-items: center; justify-content: center; }
  .crit-text { font-size: 15px; font-weight: 700; line-height: 1.35; padding-top: 3px; }
  .crit-status { padding-top: 3px; }
  .crit-body { padding: 12px 16px 14px 62px; }
  .crit-body > * + * { margin-top: 10px; }
  .why { margin: 0; font-size: 13px; color: #3a3a42; }
  .label { font-size: 11px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: #6b6b73; }
  .callout { padding: 10px 14px; border-radius: 8px; font-size: 13px; line-height: 1.5; border: 1px solid; }
  .callout.info { background: #eef6fc; border-color: #1c5f8a40; color: #163f5a; }
  .callout.warn { background: #fffbea; border-color: #854f0b40; color: #5e3a06; }
  .callout-label { font-size: 11px; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase; margin-bottom: 3px; }
  ul.checks { list-style: none; margin: 4px 0 0; padding: 0; display: grid; gap: 6px; }
  ul.checks li { font-size: 12px; color: #3a3a42; padding: 6px 10px; border: 1px solid #ececf0; border-radius: 6px; background: #fff; }
  .check-line { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; }
  .counts { font-size: 11px; color: #6b6b73; }
  .label-tail { font-weight: 600; letter-spacing: 0; text-transform: none; }
  details.fold { margin-top: 6px; }
  details.fold > summary { cursor: pointer; font-size: 12px; font-weight: 600; color: #1c5f8a; list-style: none; }
  details.fold > summary::-webkit-details-marker { display: none; }
  details.fold > summary::before { content: '▸ '; color: #8a8a93; }
  details.fold[open] > summary::before { content: '▾ '; }
  ul.checks li > div:not(.check-line) { margin-top: 2px; }
  .evidence-line { font-size: 12px; color: #2a2a32; }
  .crit-verdict { margin: 0; font-size: 13px; color: #1b1b1f; }
  .params { font-size: 11px; margin: 2px 0 4px 14px; }
  .param { padding: 1px 0; word-break: break-all; }
  .param.hot { background: #fff0f0; border-left: 3px solid #8a1a1a; padding-left: 6px; }
  .hot-mark { color: #8a1a1a; font-size: 11px; }
  .param.checked { background: #eef6ff; border-left: 3px solid #1f5fa8; padding-left: 6px; }
  .checked-mark { color: #1f5fa8; font-size: 11px; }
  .a3p-entry { padding: 2px 0 2px 9px; border-left: 3px solid transparent; }
  .a3p-entry.hot { background: #eef6ff; border-left-color: #1f5fa8; }
  .a3p-token { font-family: ui-monospace, Menlo, monospace; font-size: 10.5px; margin: 2px 0; white-space: pre-wrap; word-break: break-all; }
  .a3p-token b { background: #fff3c4; }
  .check-sentence { font-size: 12px; color: #1b1b1f; margin-bottom: 2px; }
  .evidence { font-size: 12px; }
  table.inventory { width: 100%; border-collapse: collapse; font-size: 11px; margin: 6px 0 18px; }
  table.inventory th, table.inventory td { border-bottom: 1px solid #e5e5ea; padding: 4px 6px; text-align: left; vertical-align: top; word-break: break-word; }
  table.inventory .ok { color: #1a6f3c; } table.inventory .bad { color: #a11; font-weight: 600; }

  ul.ack { list-style: none; margin: 0; padding: 0; }
  ul.ack li { display: flex; gap: 12px; align-items: flex-start; padding: 10px 0; border-bottom: 1px solid #ececf0; }
  ul.ack input { width: 18px; height: 18px; margin: 2px 0 0; flex-shrink: 0; }
  ul.ack label { flex: 1; cursor: pointer; font-size: 14px; font-weight: 600; line-height: 1.4; }
  .ack-num { color: #6b6b73; font-weight: 700; margin: 0 2px 0 6px; }

  .gate { padding: 12px 16px; border-radius: 8px; font-size: 13px; font-weight: 700; border: 1px solid; margin-bottom: 14px; }
  .gate.warn { background: #fffbea; border-color: #854f0b55; color: #5e3a06; }
  .gate.fail { background: #fff0f0; border-color: #8a1a1a55; color: #8a1a1a; }
  .stamp { padding: 14px 18px; border-radius: 8px; background: #edfaf3; border: 1px solid #1a6f3c40; margin-bottom: 14px; }
  .stamp-title { font-size: 16px; font-weight: 800; color: #1a6f3c; }
  .stamp-meta { font-size: 12px; color: #3a3a42; margin-top: 4px; }
  .sig-grid { display: grid; grid-template-columns: 2fr 1.5fr 1fr; gap: 24px; margin-top: 26px; }
  .sig-line { border-bottom: 1px solid #1b1b1f; height: 28px; }
  .sig-label { font-size: 11px; color: #6b6b73; margin-top: 4px; text-transform: uppercase; letter-spacing: 0.06em; }

  .def { padding: 10px 0; border-bottom: 1px solid #ececf0; font-size: 12px; }
  .def-id { display: flex; align-items: center; gap: 8px; }
  .sev { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: #55555d; border: 1px solid #c9c9d1; border-radius: 3px; padding: 0 5px; }
  .def-stmt { color: #1b1b1f; font-weight: 600; margin-top: 4px; }
  .def-desc { color: #3a3a42; margin-top: 2px; }
  .def-kv { color: #55555d; margin-top: 2px; }

  table { width: 100%; border-collapse: collapse; font-size: 13px; }
  thead th { background: #f4f4f6; padding: 8px 10px; text-align: left; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: #55555d; border-bottom: 1px solid #e3e3e8; }
  tbody td { padding: 8px 10px; vertical-align: top; border-bottom: 1px solid #ececf0; }

  footer { margin-top: 36px; padding-top: 14px; border-top: 1px solid #ececf0; font-size: 11px; color: #8a8a93; display: flex; justify-content: space-between; gap: 16px; flex-wrap: wrap; }

  @media (max-width: 640px) {
    body { padding: 24px 16px 40px; }
    .crit-body { padding-left: 16px; }
    .sig-grid { grid-template-columns: 1fr; gap: 18px; }
    .verdict-box { grid-template-columns: 1fr; }
  }
  @media print {
    body { padding: 0; max-width: none; font-size: 12px; }
    .no-print { display: none !important; }
    details.section { display: block; }
    details.fold > summary { display: none; }
    details.section > summary::before { content: ''; }
    .crit-head, ul.checks li > .check-sentence, .param { break-inside: avoid; }
    a { color: inherit; }
    input[type=checkbox] { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  }
</style>
</head>
<body data-fails="${hardFails}" data-acks="${ackTotal}">
  <div class="top">
    <div>
      <div class="eyebrow">AdLens FX · Launch sign-off report</div>
      <h1>${esc(L.title || 'Untitled launch')}</h1>
    </div>
    <button class="print-btn no-print" onclick="window.print()">Print / PDF</button>
  </div>
  <div class="meta-grid">
    ${L.ticket ? `<div class="kv"><div class="k">Ticket</div><div class="v">${esc(L.ticket)}</div></div>` : ''}
    ${appLabel ? `<div class="kv"><div class="k">App</div><div class="v">${esc(appLabel)}</div></div>` : ''}
    ${L.build ? `<div class="kv"><div class="k">Build</div><div class="v">${esc(L.build)}</div></div>` : ''}
    ${waivedLine ? `<div class="kv"><div class="k">Waived</div><div class="v">${esc(waivedLine)}</div></div>` : ''}
    <div class="kv"><div class="k">Tester</div><div class="v">${esc(tester) || '<span class="muted">—</span>'}</div></div>
    <div class="kv"><div class="k">Generated</div><div class="v">${esc(dateStr)} · ${esc(timeStr)}</div></div>
    <div class="kv"><div class="k">Run${runIdsList.length === 1 ? '' : 's'}</div><div class="v">${runIdsHtml}</div></div>
    ${captureBits.join('')}
  </div>

  <div class="verdict-box">
    <div class="verdict">${overall}</div>
    <div class="verdict-meaning">${esc(verdictMeaning)}</div>
    <div class="verdict-counts">
      <span class="pill" style="color:#1a6f3c;background:#edfaf3;border-color:#1a6f3c40">${rollup.met} met</span>
      <span class="pill" style="color:#8a1a1a;background:#fff0f0;border-color:#8a1a1a40">${rollup.notMet} not met</span>
      <span class="pill" style="color:#854f0b;background:#fffbea;border-color:#854f0b40">${rollup.needsReview} needs review</span>
      <span class="pill" style="color:#1c5f8a;background:#eef6fc;border-color:#1c5f8a40">${rollup.manualReview} manual review</span>
      <span class="pill" style="color:#555;background:#f5f5f5;border-color:#55555540">${rollup.notRun} not run</span>
      <span class="pill muted">${criteria.length} criteri${criteria.length === 1 ? 'on' : 'a'}</span>
    </div>
  </div>

  ${todoHtml}
  ${criteriaSectionHtml}
  ${branchHtml}
  ${observationsHtml}
  ${limitsHtml}
  ${sdkBiddingHtml}
  ${ackSectionHtml}
  ${signoffBlockHtml}
  ${requestInventoryHtml(rows, resultsByCheckId, checkDefsById, winners)}
  ${checksTestedHtml}
  ${appendixHtml}

  <footer>
    <span>Generated by AdLens FX on ${esc(dateStr)} at ${esc(timeStr)}. Wording follows docs/signoff-report-template.md. Met = every mapped check passed · Not met = any mapped check failed · Needs review = traffic neither proved nor disproved it · Manual = not provable from network traffic.</span>
    <span>adlensfx.com</span>
  </footer>

  <script>
    (function () {
      var body = document.body;
      var fails = parseInt(body.getAttribute('data-fails') || '0', 10);
      function refresh() {
        var boxes = Array.prototype.slice.call(document.querySelectorAll('.ack-check'));
        var done = boxes.filter(function (b) { return b.checked; }).length;
        var gate = document.getElementById('signoff-gate');
        var stamp = document.getElementById('signoff-stamp');
        var blocked = document.getElementById('signoff-blocked');
        gate.style.display = 'none'; stamp.style.display = 'none'; blocked.style.display = 'none';
        if (fails > 0) { blocked.style.display = 'block'; return; }
        if (done < boxes.length) {
          gate.style.display = 'block';
          gate.textContent = (boxes.length - done) + ' of ' + boxes.length + ' item' + (boxes.length - done === 1 ? '' : 's') + ' still to acknowledge before sign-off.';
        } else {
          stamp.style.display = 'block';
        }
      }
      document.addEventListener('change', function (e) {
        if (e.target && e.target.classList && e.target.classList.contains('ack-check')) refresh();
      });
      // A printed or PDF copy must carry everything: open every folded
      // list before the print dialog renders the page.
      window.addEventListener('beforeprint', function () {
        // Open folded lists, but not each request's parameter list or the pooled
        // evidence (failing requests are already open) — that was most of 87 pages.
        Array.prototype.slice.call(document.querySelectorAll('details')).forEach(function (d) {
          if (!d.classList.contains('req-url') && !d.classList.contains('evidence-all')) d.open = true;
        });
      });
      // Inflate each gzipped a3p bid token and print the fields a reviewer asks
      // about (SDK, consent, ad id); the whole token follows, folded.
      Array.prototype.slice.call(document.querySelectorAll('.a3p-token[data-gz]')).forEach(function (el) {
        try {
          var b = atob(el.getAttribute('data-gz').replace(/-/g, '+').replace(/_/g, '/'));
          var bytes = new Uint8Array(b.length);
          for (var i = 0; i < b.length; i++) bytes[i] = b.charCodeAt(i);
          if (typeof DecompressionStream === 'undefined') return;
          new Response(new Blob([bytes]).stream().pipeThrough(new DecompressionStream('gzip'))).text().then(function (txt) {
            var j = JSON.parse(txt);
            var g = function (o, p) { return p.split('.').reduce(function (a, k) { return a == null ? a : a[k]; }, o); };
            var e = function (v) { return String(v).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); };
            var keys = [['SDK', 'request.sdk_user_agent'], ['us privacy (ccpa)', 'user.ccpa.status'], ['gdpr consent', 'user.gdpr.consent_status'],
              ['limit ad tracking', 'device.lmt'], ['ad id', 'device.ifa'], ['device', 'device.model'], ['os', 'device.osv'], ['view #', 'ordinal_view']];
            var lines = keys.filter(function (k) { return g(j, k[1]) != null && g(j, k[1]) !== ''; })
              .map(function (k) { return e(k[0]) + ': <b>' + e(g(j, k[1])) + '</b>'; });
            el.innerHTML = 'bid token (decoded): ' + lines.join(' · ')
              + '<details class="req-url"><summary>full token</summary>' + e(JSON.stringify(j, null, 1)) + '</details>';
          }).catch(function () { /* leave the placeholder */ });
        } catch (err) { /* leave the placeholder */ }
      });
      refresh();
    })();
  </script>
</body>
</html>`;
}

/** Open report in a new tab (print-to-PDF friendly). */
export function openLaunchReport(launch, resultsByCheckId, meta = {}) {
  const html = buildLaunchReport(launch, resultsByCheckId, meta);
  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}

/** Download report as a standalone HTML file. */
export function downloadLaunchReport(launch, resultsByCheckId, meta = {}) {
  const html = buildLaunchReport(launch, resultsByCheckId, meta);
  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const date = new Date().toISOString().slice(0, 10);
  const id = (launch && launch.id) || 'launch';
  a.href = url;
  a.download = `launch-signoff-${id}-${date}.html`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
